import { useCallback, type ReactNode } from 'react';
import { DsProvider } from '@workpay/ds-shared/context/DsProvider';
import { getDirection } from '@workpay/ds-shared/utils/direction';
import { dsTemplateService } from '@services/dsTemplateService';
import { dsEnvelopeService } from '@services/dsEnvelopeService';
import { dsReviewService } from '@services/dsReviewService';
import { dsFileService } from '@services/dsFileService';
import { dsAuditService } from '@services/dsAuditService';
import { dsEditorService } from '@services/dsEditorService';
import { dsPlaceholderCatalogService } from '@services/dsPlaceholderCatalogService';
import { useTranslation } from '@hooks/useTranslation';
import { useNotificationStore } from '@stores/notificationStore';
import { useAuthStore } from '@stores/authStore';
import { useLocalizationStore } from '@stores/localizationStore';
import { ONLYOFFICE_URL } from '@/lib/runtimeConfig';

const services = {
  template: dsTemplateService,
  envelope: dsEnvelopeService,
  review: dsReviewService,
  file: dsFileService,
  audit: dsAuditService,
  editor: dsEditorService,
  placeholderCatalog: dsPlaceholderCatalogService,
};

export function AppDsProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const addNotification = useNotificationStore((s) => s.addNotification);
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const language = useLocalizationStore((s) => s.language);
  const direction = getDirection(language);

  const notify = useCallback(
    ({ variant, title }: { variant: 'success' | 'critical'; title: string }) =>
      addNotification({ variant, title }),
    [addNotification],
  );

  const navigate = useCallback((path: string) => {
    window.location.assign(path);
  }, []);

  return (
    <DsProvider
      services={services}
      translate={t}
      notify={notify}
      hasPermission={hasPermission}
      navigate={navigate}
      onlyOfficeUrl={ONLYOFFICE_URL}
      direction={direction}
    >
      {children}
    </DsProvider>
  );
}
